import _ from 'lodash';
import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import type { AxiosResponse } from 'axios';
import {
	AlertNotificationModuleConstant,
	ExperienceTypeConstant,
	StorageConstant
} from '../../constants';
import { alertNotificationService } from '../../services/alertNotificationService';
import { clubNotificationService } from '../../services/clubNotificationService';
import { featureFlagService } from '../../services/featureFlagService';
import { storageService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IAlertNotificationState {
	alertNotifications: ltFramework.IAlertNotification[];
}

const AlertNotificationState: IAlertNotificationState = {
	alertNotifications: new Array<ltFramework.IAlertNotification>()
};

const { action, mutation } = AlertNotificationModuleConstant;

export const AlertNotificationMutations: MutationTree<IAlertNotificationState> = {
	[mutation.setAlertNotifications](
		state: IAlertNotificationState,
		payload: ltFramework.IAlertNotification[]
	) {
		state.alertNotifications = payload;
	},

	[mutation.addAlertNotification](
		state: IAlertNotificationState,
		payload: ltFramework.IAlertNotification
	) {
		state.alertNotifications.push(payload);
	},

	[mutation.removeAlertNotification](
		state: IAlertNotificationState,
		payload: ltFramework.IAlertNotification
	) {
		const index: number = _.findIndex(state.alertNotifications, { id: payload.id });
		if (index !== -1) {
			state.alertNotifications.splice(index, 1);
		}
	}
};

export const AlertNotificationActions: ActionTree<IAlertNotificationState, ltFramework.IRootState>
	= {
		[action.initialize](
			{ commit }: ActionContext<IAlertNotificationState, ltFramework.IRootState>,
			clubId: number
		): Promise<void> {
			return new Promise<void>((resolve) => {
				const deferredAlertNotifications: ltFramework.IAlertNotification[]
					= storageService.sessionGet<ltFramework.IAlertNotification[]>(
						StorageConstant.deferredAlertNotifications
					) as ltFramework.IAlertNotification[];
				const dismissedAlertNotifications: ltFramework.IDismissedAlertNotification[]
					= storageService.localGet<ltFramework.IDismissedAlertNotification[]>(
						StorageConstant.dismissedAlertNotifications
					) as ltFramework.IDismissedAlertNotification[];
				const isApp = window.lt.pageProperties.selectors.includes('app');
				const disablePageNotifications = window.lt.pageProperties.disablePageNotifications;
				const experienceType: string = window.lt.pageProperties.experienceType
					? window.lt.pageProperties.experienceType.toLowerCase()
					: '';
				// clean out expired dismissed notifications
				alertNotificationService.clearExpiredDismissedNotifications(dismissedAlertNotifications);
				const { author, editMode } = window.lt || ({} as ltFramework.ILifeTime);
				const publishMode = !author || (author && !editMode);
				const isMemberType = experienceType === ExperienceTypeConstant.member;
				const disableClubNotifications = featureFlagService.isEnabled('aemMyLtDisableClubNotifications');
				if (clubId && !isApp && publishMode && !disablePageNotifications && isMemberType && !disableClubNotifications) {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					clubNotificationService
						.getClubNotifications(clubId)
						.then((response: AxiosResponse<ltFramework.IAlertNotification[]>) => {
							_.forEach(response.data, (notification: ltFramework.IAlertNotification) => {
								notification.defer = false;
								notification.disableScroll = true;
								alertNotificationService.openAlertNotification(notification, []);
							});

							commit(mutation.setAlertNotifications, response.data);
						});
				}

				if (deferredAlertNotifications) {
					storageService.sessionRemove(StorageConstant.deferredAlertNotifications);

					_.forEach(
						deferredAlertNotifications,
						(alertNotification: ltFramework.IAlertNotification) => {
							alertNotification.defer = false;
							alertNotificationService.openAlertNotification(alertNotification, []);
						}
					);

					commit(mutation.setAlertNotifications, deferredAlertNotifications);
				}
				else {
					commit(mutation.setAlertNotifications, new Array<ltFramework.IAlertNotification>());
				}

				const dismissedNotificationIds: string[] = _.map(dismissedAlertNotifications, 'id');
				alertNotificationService.displayAuthoredNotifications(dismissedNotificationIds);

				resolve();
			});
		},

		[action.open](
			{ commit, state }: ActionContext<IAlertNotificationState, ltFramework.IRootState>,
			payload: ltFramework.IAlertNotification
		): Promise<void> {
			return new Promise<void>((resolve) => {
				const alertNotifications: ltFramework.IAlertNotification[] = state.alertNotifications;
				const alertNotification = alertNotificationService.openAlertNotification(
					payload,
					alertNotifications
				);

				commit(mutation.addAlertNotification, alertNotification);
				resolve();
			});
		},

		[action.close](
			{ commit }: ActionContext<IAlertNotificationState, ltFramework.IRootState>,
			payload: ltFramework.IAlertNotification
		): Promise<void> {
			return new Promise<void>((resolve) => {
				alertNotificationService.closeAlertNotification(payload);

				commit(mutation.removeAlertNotification, payload);
				resolve();
			});
		}
	};

export const AlertNotificationModule: Module<IAlertNotificationState, ltFramework.IRootState> = {
	namespaced: true,
	state: AlertNotificationState,
	actions: AlertNotificationActions,
	mutations: AlertNotificationMutations
};
